import React, { useState, useEffect } from 'react';
import { BRAND } from './product';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface LoginFormProps {
  onToggleMode: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');
  const [invitationEmail, setInvitationEmail] = useState('');

  // Load invitation details if token is present
  useEffect(() => {
    if (invitationToken) {
      fetch(`/api/teams/invitation/${invitationToken}`)
        .then(res => res.json())
        .then(data => {
          if (data.email) {
            setInvitationEmail(data.email);
            setEmail(data.email); // Pre-fill the email
          }
        })
        .catch(err => console.error('Failed to load invitation:', err));
    }
  }, [invitationToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      // After successful login, redirect to invitation if token exists
      if (invitationToken) {
        navigate(`/invitation/${invitationToken}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={BRAND.icon} alt={BRAND.name} className="w-12 h-12 rounded-xl mx-auto mb-4 shadow-lg" />
          <h2 className="text-2xl font-bold text-white tracking-tight">Sign in to {BRAND.name}</h2>
          <p className="text-sm text-slate-400 mt-2">Welcome back. Enter your details below.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-modal p-8">
          {invitationEmail && (
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200/60 rounded-xl">
              <p className="text-sm text-blue-800">
                <strong>Team Invitation:</strong> You've been invited to join a team. Please log in with <strong>{invitationEmail}</strong>.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <a
                  href="/forgot-password"
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  Forgot password?
                </a>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200/60 rounded-xl p-3">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Don't have an account?{' '}
            {invitationToken ? (
              <button
                onClick={onToggleMode}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Sign up
              </button>
            ) : (
              <a
                href={BRAND.pricingUrl}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Get Started
              </a>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

interface RegisterFormProps {
  onToggleMode: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onToggleMode }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');
  const [invitationEmail, setInvitationEmail] = useState('');

  // Load invitation details if token is present
  useEffect(() => {
    if (invitationToken) {
      fetch(`/api/teams/invitation/${invitationToken}`)
        .then(res => res.json())
        .then(data => {
          if (data.email) {
            setInvitationEmail(data.email);
            // Pre-fill the email
            setFormData(prev => ({ ...prev, email: data.email }));
          }
        })
        .catch(err => console.error('Failed to load invitation:', err));
    }
  }, [invitationToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await register(formData.email, formData.password, formData.firstName, formData.lastName);
      // After successful registration, redirect to invitation if token exists
      if (invitationToken) {
        navigate(`/invitation/${invitationToken}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={BRAND.icon} alt={BRAND.name} className="w-12 h-12 rounded-xl mx-auto mb-4 shadow-lg" />
          <h2 className="text-2xl font-bold text-white tracking-tight">Create your account</h2>
          <p className="text-sm text-slate-400 mt-2">Get started with {BRAND.name} in minutes.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-modal p-8">
          {invitationEmail && (
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200/60 rounded-xl">
              <p className="text-sm text-blue-800">
                <strong>Team Invitation:</strong> You've been invited to join a team. Please sign up with <strong>{invitationEmail}</strong>.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="input"
              />
              <p className="text-xs text-gray-400 mt-1.5">Minimum 6 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200/60 rounded-xl p-3">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <button
              onClick={onToggleMode}
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Complete Registration form for users who paid via Stripe but need to set password
export const CompleteRegistrationForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rawEmailFromUrl = searchParams.get('email') || '';
  // Older links (and any Stripe misconfig) can carry the literal, unsubstituted
  // placeholder "{CHECKOUT_SESSION_CUSTOMER_EMAIL}" — ignore it.
  const emailFromUrl = rawEmailFromUrl && !rawEmailFromUrl.includes('{') ? rawEmailFromUrl : '';
  const sessionId = searchParams.get('session_id') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(!!emailFromUrl || !!sessionId);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState<{ firstName?: string; needsRegistration?: boolean } | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Resolve the customer email — directly from ?email, or by looking up the
  // Stripe checkout session from ?session_id (the only placeholder Stripe
  // actually substitutes) — then check their registration status.
  useEffect(() => {
    if (emailFromUrl) {
      checkRegistrationStatus(emailFromUrl);
    } else if (sessionId) {
      resolveSessionThenCheck(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromUrl, sessionId]);

  const resolveSessionThenCheck = async (sid: string, retryCount = 0): Promise<void> => {
    setChecking(true);
    try {
      const resp = await fetch(`/api/stripe/checkout-email/${encodeURIComponent(sid)}`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.email) {
          setEmail(data.email);
          return checkRegistrationStatus(data.email);
        }
      }
    } catch { /* fall through to retry */ }
    // The email may not be on the session immediately after redirect — retry.
    if (retryCount < 5) {
      await new Promise(r => setTimeout(r, 2000));
      return resolveSessionThenCheck(sid, retryCount + 1);
    }
    setError('Could not load your registration. Please use the link in your welcome email to set your password.');
    setChecking(false);
  };

  const checkRegistrationStatus = async (emailToCheck: string, retryCount = 0): Promise<void> => {
    setChecking(true);
    try {
      const response = await fetch(`/api/auth/check-registration/${encodeURIComponent(emailToCheck)}`);
      const data = await response.json();
      setUserInfo(data);

      if (data.exists && data.hasPassword) {
        // Already has password, redirect to login
        setError('Account already activated. Redirecting to login...');
        setTimeout(() => navigate('/auth'), 2000);
      } else if (!data.exists) {
        // Webhook may not have fired yet — retry up to 5 times (15s total)
        if (retryCount < 5) {
          setError('');
          await new Promise(r => setTimeout(r, 3000));
          return checkRegistrationStatus(emailToCheck, retryCount + 1);
        }
        setError('No account found for this email. Please complete payment first.');
      } else if (!data.hasSubscription) {
        setError('No active subscription found. Please complete payment first.');
      }
    } catch (err) {
      console.error('Failed to check registration:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/complete-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete registration');
      }

      // Store token and redirect
      localStorage.setItem('token', data.token);
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-white mx-auto mb-4"></div>
          <div className="text-slate-300 text-sm">Setting up your account...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 ring-1 ring-emerald-500/20">
            <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Payment Successful!</h2>
          <p className="text-slate-400 mt-2 text-sm">
            {userInfo?.firstName ? `Welcome ${userInfo.firstName}! ` : ''}
            Set your password to access {BRAND.name}.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-modal p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => email && checkRegistrationStatus(email)}
                required
                className="input bg-gray-50"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Create Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="input"
                placeholder="Minimum 6 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="input"
                placeholder="Confirm your password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200/60 rounded-xl p-3">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !userInfo?.needsRegistration}
              className="btn-primary w-full py-2.5"
            >
              {loading ? 'Activating...' : 'Activate Account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have a password?{' '}
            <a href="/auth" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export const AuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const hasInvitation = searchParams.get('invitation');

  // Default to signup if there's an invitation (most invitations are for new users)
  const [isLogin, setIsLogin] = useState(!hasInvitation);

  const toggleMode = () => setIsLogin(!isLogin);

  return isLogin ? (
    <LoginForm onToggleMode={toggleMode} />
  ) : (
    <RegisterForm onToggleMode={toggleMode} />
  );
};

